import { isEqual } from 'lodash';
import {
  CustomTransformOperator,
  DataFrame,
  DataTopic,
  DataTransformerConfig,
  LoadingState,
  PanelData,
  ScopedVars,
  transformDataFrame,
} from '@grafana/data';
import { toDataQueryError } from '@grafana/runtime';
import { catchError, forkJoin, map, of, ReplaySubject, Unsubscribable } from 'rxjs';
import { sceneGraph } from '../core/sceneGraph';
import { SceneObjectBase } from '../core/SceneObjectBase';
import { CustomTransformerDefinition, SceneDataProvider, SceneDataProviderResult, SceneDataState } from '../core/types';
import { VariableDependencyConfig } from '../variables/VariableDependencyConfig';
import { SceneDataLayerSet } from './SceneDataLayerSet';
import { findPanelProfiler } from '../utils/findPanelProfiler';

export interface SceneDataTransformerState extends SceneDataState {
  /**
   * Array of standard transformation configs and custom transform operators
   */
  transformations: Array<DataTransformerConfig | CustomTransformerDefinition>;
}

/**
 * You can use this as a $data object. It can either transform an inner $data DataProvider or if that is not set it will
 * subscribe to a DataProvider higher up in the scene graph and transform its data.
 *
 * The transformations array supports custom (runtime defined) transformation as well as declarative core transformations.
 * You can manually re-process the transformations by calling reprocessTransformations(). This is useful if you have
 * transformations that depend on other scene object states.
 */
export class SceneDataTransformer extends SceneObjectBase<SceneDataTransformerState> implements SceneDataProvider {
  private _transformSub?: Unsubscribable;
  private _results = new ReplaySubject<SceneDataProviderResult>(1);
  private _prevDataFromSource?: PanelData;

  /**
   * Scan transformations for variable usage and re-process transforms when a variable values change
   */
  protected _variableDependency: VariableDependencyConfig<SceneDataTransformerState> = new VariableDependencyConfig(
    this,
    {
      statePaths: ['transformations'],
      onReferencedVariableValueChanged: () => this.reprocessTransformations(),
    }
  );

  public constructor(state: SceneDataTransformerState) {
    super(state);

    this.addActivationHandler(() => this.activationHandler());
  }

  private activationHandler() {
    const sourceData = this.getSourceData();

    this._subs.add(sourceData.subscribeToState((state) => this.transform(state.data)));

    if (sourceData.state.data) {
      this.transform(sourceData.state.data);
    }

    return () => {
      if (this._transformSub) {
        this._transformSub.unsubscribe();
      }
    };
  }

  private getSourceData(): SceneDataProvider {
    if (this.state.$data) {
      if (this.state.$data instanceof SceneDataLayerSet) {
        throw new Error('SceneDataLayerSet can not be used as data provider for SceneDataTransformer.');
      }
      return this.state.$data;
    }

    if (!this.parent || !this.parent.parent) {
      throw new Error('SceneDataTransformer must either have $data set on it or have a parent.parent with $data');
    }

    return sceneGraph.getData(this.parent.parent);
  }

  public setContainerWidth(width: number) {
    if (this.state.$data && this.state.$data.setContainerWidth) {
      this.state.$data.setContainerWidth(width);
    }
  }

  public isDataReadyToDisplay() {
    const dataObject = this.getSourceData();
    if (dataObject.isDataReadyToDisplay) {
      return dataObject.isDataReadyToDisplay();
    }

    return true;
  }

  public reprocessTransformations() {
    this.transform(this.getSourceData().state.data, true);
  }

  /**
   * S3.1: Calculate transformation complexity metrics
   */
  private _calculateTransformationMetrics(
    data: PanelData,
    transformations: Array<DataTransformerConfig | CustomTransformerDefinition>
  ): {
    transformationCount: number;
    seriesTransformationCount: number;
    annotationTransformationCount: number;
  } {
    const transformationCount = transformations.length;

    // Count transformations by topic (series vs annotations)
    const seriesTransformationCount = transformations.filter((transformation) => {
      if ('options' in transformation || 'topic' in transformation) {
        return transformation.topic == null || transformation.topic === DataTopic.Series;
      }
      return true; // Custom transformations default to series
    }).length;

    const annotationTransformationCount = transformations.filter((transformation) => {
      if ('options' in transformation || 'topic' in transformation) {
        return transformation.topic === DataTopic.Annotations;
      }
      return false;
    }).length;

    return {
      transformationCount,
      seriesTransformationCount,
      annotationTransformationCount,
    };
  }

  public cancelQuery() {
    this.getSourceData().cancelQuery?.();
  }

  public getResultsStream() {
    return this._results;
  }

  public clone(withState?: Partial<SceneDataTransformerState>) {
    const clone = super.clone(withState);

    if (this._prevDataFromSource) {
      clone['_prevDataFromSource'] = this._prevDataFromSource;
    }

    return clone;
  }

  public isInViewChanged(isInView: boolean) {
    this.state.$data?.isInViewChanged?.(isInView);
  }

  public bypassIsInViewChanged(bypassIsInView: boolean) {
    this.state.$data?.bypassIsInViewChanged?.(bypassIsInView);
  }

  private haveAlreadyTransformedData(data: PanelData) {
    if (!this._prevDataFromSource) {
      return false;
    }

    if (data === this._prevDataFromSource) {
      return true;
    }

    const { series, annotations } = this._prevDataFromSource;

    if (data.series === series && data.annotations === annotations) {
      if (this.state.data) {
        const currentData = this.state.data;
        const nextData: PanelData = {
          ...data,
          series: currentData.series,
          annotations: currentData.annotations,
        };

        const metadataChanged =
          currentData.state !== nextData.state ||
          currentData.request?.requestId !== nextData.request?.requestId ||
          currentData.error !== nextData.error ||
          !isEqual(currentData.errors, nextData.errors) ||
          !isEqual(currentData.timeRange, nextData.timeRange);

        if (metadataChanged) {
          this.setState({ data: nextData });
          this._results.next({ origin: this, data: nextData });
        }
      }

      return true;
    }

    return false;
  }

  private transform(data: PanelData | undefined, force = false) {
    const timestamp = performance.now();
    // S3.1: Performance tracking entry point
    const profiler = findPanelProfiler(this);
    const transformStartTime = performance.now();
    let transformationId: string | undefined;
    let endTransformCallback:
      | ((
          endTimestamp: number,
          duration: number,
          success: boolean,
          result?: {
            outputSeriesCount?: number;
            outputAnnotationsCount?: number;
            error?: string;
          }
        ) => void)
      | null = null;

    if (this.state.transformations.length === 0 || !data) {
      this._prevDataFromSource = data;
      this.setState({ data });

      if (data) {
        this._results.next({ origin: this, data });
      }
      return;
    }

    // Skip transform step if we have already transformed this data
    if (!force && this.haveAlreadyTransformedData(data)) {
      return;
    }

    // S3.1: Start transformation tracking
    if (profiler) {
      // Create meaningful transformation identifier from actual transformations
      const transformationTypes = this.state.transformations
        .map((t) => {
          if ('id' in t) {
            // Standard DataTransformerConfig
            return t.id;
          } else {
            // CustomTransformerDefinition
            return 'customTransformation';
          }
        })
        .join('+');
      transformationId = transformationTypes || 'no-transforms';

      // Calculate transformation complexity metrics
      const metrics = this._calculateTransformationMetrics(data, this.state.transformations);

      // Start the DataProcessing phase with centralized logging - get end callback
      endTransformCallback = profiler.onDataTransformStart(timestamp, transformationId, metrics);
    }

    const interpolatedTransformations = this._interpolateVariablesInTransformationConfigs(data);

    const seriesTransformations = this._filterAndPrepareTransformationsByTopic(
      interpolatedTransformations,
      (transformation) => {
        if ('options' in transformation || 'topic' in transformation) {
          return transformation.topic == null || transformation.topic === DataTopic.Series;
        }
        return true;
      }
    );
    const annotationsTransformations = this._filterAndPrepareTransformationsByTopic(
      interpolatedTransformations,
      (transformation) => {
        if ('options' in transformation || 'topic' in transformation) {
          return transformation.topic === DataTopic.Annotations;
        }
        return false;
      }
    );

    if (this._transformSub) {
      this._transformSub.unsubscribe();
    }

    const ctx = {
      interpolate: (value: string, scopedVars?: ScopedVars) => {
        return sceneGraph.interpolate(this, value, { ...data.request?.scopedVars, ...scopedVars });
      },
    };

    const seriesStream = transformDataFrame(seriesTransformations, data.series, ctx);
    const annotationsStream = transformDataFrame(annotationsTransformations, data.annotations ?? []);

    let series: DataFrame[] = [];
    let annotations: DataFrame[] = [];

    this._transformSub = forkJoin([seriesStream, annotationsStream])
      .pipe(
        map((results) => {
          // this strategy allows transformations to take in series frames and produce anno frames
          // we look at each transformation's result and put it in the correct place
          results.forEach((frames) => {
            for (const frame of frames) {
              if (frame.meta?.dataTopic === DataTopic.Annotations) {
                annotations.push(frame);
              } else {
                series.push(frame);
              }
            }
          });

          return { ...data, series, annotations };
        }),
        catchError((err) => {
          const timestamp = performance.now();
          // S3.1: Performance tracking for transformation errors
          const duration = timestamp - transformStartTime;

          if (endTransformCallback) {
            // End the DataProcessing phase with centralized logging using callback
            endTransformCallback(timestamp, duration, false, {
              error: err.message || err,
            });
          }

          console.error('Error transforming data: ', err);
          const sourceErr = this.getSourceData().state.data?.errors || [];

          const transformationError = toDataQueryError(err);
          transformationError.message = `Error transforming data: ${transformationError.message}`;

          const result: PanelData = {
            ...data,
            state: LoadingState.Error,
            // Combine transformation error with upstream errors
            errors: [...sourceErr, transformationError],
          };

          return of(result);
        })
      )
      .subscribe((transformedData) => {
        const timestamp = performance.now();
        const duration = timestamp - transformStartTime;
        if (endTransformCallback) {
          // End the DataProcessing phase with centralized logging using callback
          endTransformCallback(timestamp, duration, true, {
            outputSeriesCount: transformedData.series.length,
            outputAnnotationsCount: transformedData.annotations?.length || 0,
          });
        }
        this.setState({ data: transformedData });
        this._results.next({ origin: this, data: transformedData });
        this._prevDataFromSource = data;
      });
  }

  private _interpolateVariablesInTransformationConfigs(
    data: PanelData
  ): Array<DataTransformerConfig | CustomTransformerDefinition> {
    const transformations = this.state.transformations;

    if (this._variableDependency.getNames().size === 0) {
      return transformations;
    }

    const onlyObjects = transformations.every((t) => typeof t === 'object');

    // If all transformations are config object we can interpolate them all at once
    if (onlyObjects) {
      return JSON.parse(sceneGraph.interpolate(this, JSON.stringify(transformations), data.request?.scopedVars));
    }

    return transformations.map((t) => {
      return typeof t === 'object'
        ? JSON.parse(sceneGraph.interpolate(this, JSON.stringify(t), data.request?.scopedVars))
        : t;
    });
  }

  private _filterAndPrepareTransformationsByTopic(
    interpolatedTransformations: Array<DataTransformerConfig<any> | CustomTransformerDefinition>,
    transformationFilter: (transformation: DataTransformerConfig<any> | CustomTransformerDefinition) => boolean
  ): Array<DataTransformerConfig<any> | CustomTransformOperator> {
    return interpolatedTransformations
      .filter(transformationFilter)
      .map((transformation) => ('operator' in transformation ? transformation.operator : transformation));
  }
}
